import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ExternalLink, FileUp, FolderKanban } from "lucide-react";
import { Link } from "react-router";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { RecordOwnerDisplay } from "@/domains/record-owners/components";
import { formatRelativeTime } from "@/shared/lib/utils";
import { openResource } from "@/domains/resources";
import { resourceQueries } from "../../queries";

export function ResourcesPage() {
  const {
    data: uploadedResources = [],
    isLoading: isLoadingUploadedResources,
    isError: isUploadedResourcesError,
  } = useQuery(resourceQueries.list());
  const {
    data: externalResources = [],
    isLoading: isLoadingExternalResources,
    isError: isExternalResourcesError,
  } = useQuery(resourceQueries.externalList());

  return (
    <>
      <title>Resources - Researchly</title>
      <div className="flex flex-col gap-6 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/5 text-primary">
              <FolderKanban className="size-5" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">
                Resources
              </h1>
              <p className="text-sm text-muted-foreground">
                Review uploaded files and external media linked to your reports.
              </p>
            </div>
          </div>
          <Button asChild className="rounded-xl px-4 sm:self-start">
            <Link to="/resources/new">
              <FileUp className="size-4" />
              Upload resource
            </Link>
          </Button>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Uploaded resources</CardTitle>
            <CardDescription>Sorted by most recently updated.</CardDescription>
          </CardHeader>
          <div className="overflow-x-auto px-3 pb-3">
            <table className="min-w-full border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Updated</th>
                  <th className="px-3 py-2 font-medium">Uploaded by</th>
                  <th className="px-3 py-2 font-medium">Open</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingUploadedResources ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      Loading uploaded resources…
                    </td>
                  </tr>
                ) : isUploadedResourcesError ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      Uploaded resources could not be loaded.
                    </td>
                  </tr>
                ) : uploadedResources.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      No uploaded resources found for this tenant yet.
                    </td>
                  </tr>
                ) : (
                  uploadedResources.map((item) => (
                    <tr
                      key={item.id}
                      className="rounded-2xl border border-border/60 bg-background"
                    >
                      <td className="rounded-l-2xl px-3 py-3 text-sm font-medium text-foreground">
                        {item.name}
                      </td>
                      <td className="px-3 py-3 text-sm text-muted-foreground">
                        <Badge variant="outline" className="rounded-full">
                          {item.type}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-sm text-muted-foreground">
                        {formatRelativeTime(item.updatedAt)}
                      </td>
                      <td className="px-3 py-3">
                        <RecordOwnerDisplay owner={item.owner} compact />
                      </td>
                      <td className="rounded-r-2xl px-3 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl"
                          onClick={() => void openResource(item)}
                        >
                          <ArrowUpRight className="size-4" />
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>External media</CardTitle>
            <CardDescription>
              Sources linked to reports via <code>analysis_media</code>.
            </CardDescription>
          </CardHeader>
          <div className="overflow-x-auto px-3 pb-3">
            <table className="min-w-full border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Source type</th>
                  <th className="px-3 py-2 font-medium">Captured</th>
                  <th className="px-3 py-2 font-medium">Used in reports</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingExternalResources ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      Loading external media…
                    </td>
                  </tr>
                ) : isExternalResourcesError ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      External media could not be loaded.
                    </td>
                  </tr>
                ) : externalResources.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="rounded-2xl border border-border/60 px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      No external media is linked to reports yet.
                    </td>
                  </tr>
                ) : (
                  externalResources.map((item) => (
                    <tr
                      key={item.id}
                      className="rounded-2xl border border-border/60 bg-background"
                    >
                      <td className="rounded-l-2xl px-3 py-3 text-sm font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 hover:text-primary"
                            >
                              <ExternalLink className="size-4 text-muted-foreground" />
                              <span>{item.title}</span>
                            </a>
                          ) : (
                            <>
                              <ExternalLink className="size-4 text-muted-foreground" />
                              <span>{item.title}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-muted-foreground">
                        <Badge variant="outline" className="rounded-full">
                          {item.sourceType}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-sm text-muted-foreground">
                        {formatRelativeTime(item.capturedAt)}
                      </td>
                      <td className="rounded-r-2xl px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          {item.reports.map((report) => (
                            <Button
                              key={report.id}
                              asChild
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-full px-3 text-xs"
                            >
                              <Link to={`/reports/${report.id}`}>
                                {report.name}
                              </Link>
                            </Button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
